/**
 * Expense category glyphs — the same drawings the Reimburse phone app uses, so
 * a category picked on one device looks the same on the other. The key is what
 * `expense_categories.icon` stores.
 *
 * Drawn on the house frame (24 grid, 1.5 stroke, round caps) rather than
 * Reimburse's 2px stroke, so they sit in a Project Hub row without looking
 * heavier than the icons around them.
 */

const P: Record<string, string> = {
  receipt: "M6 2.5h12v19l-3-1.8-3 1.8-3-1.8-3 1.8zM9 7h6M9 11h6M9 15h3",
  plane: "M12 2.5c-.9 0-1.5 1-1.5 2.3V9L3 13.5v2l7.5-2.3v4.9L8 20v1.5l4-1.1 4 1.1V20l-2.5-1.9v-4.9l7.5 2.3v-2L13.5 9V4.8c0-1.3-.6-2.3-1.5-2.3z",
  train: "M8 3h8a3 3 0 013 3v8a3 3 0 01-3 3H8a3 3 0 01-3-3V6a3 3 0 013-3zM5 10.5h14M12 3v7.5M8.5 14h.01M15.5 14h.01M8.5 17 6.5 21M15.5 17l2 4M7.6 19h8.8",
  car: "M5 17H3.5a.5.5 0 01-.5-.5v-3a2 2 0 012-2h.8l2.1-3.4A2 2 0 019.6 7.2h4.5a2 2 0 011.6.8l2.6 3.5h.7a2 2 0 012 2v3a.5.5 0 01-.5.5H19M9 17h6M6.3 11.5h11.9M12 7.2v4.3M5 17a2 2 0 104 0 2 2 0 10-4 0M15 17a2 2 0 104 0 2 2 0 10-4 0",
  fuel: "M4 21V5a2 2 0 012-2h6a2 2 0 012 2v16M3 21h12M7 7h4v4H7zM14 10h1.5a2 2 0 012 2v4.5a1.5 1.5 0 003 0V8.5L18 6",
  road: "M5 21 9.5 3M19 21 14.5 3M12 5v2.5M12 11v2.5M12 17v3",
  parking: "M6 3h12a3 3 0 013 3v12a3 3 0 01-3 3H6a3 3 0 01-3-3V6a3 3 0 013-3zM10 17V7h3.5a3 3 0 010 6H10",
  bed: "M3 5v15M3 16h18M21 20v-7a2 2 0 00-2-2h-8v5M5 11.5a2 2 0 104 0 2 2 0 10-4 0",
  meal: "M5 3v5a2 2 0 004 0V3M7 3v18M17 21V3c-2 1-3.5 3.5-3.5 7 0 2 1.5 3 3.5 3",
  box: "M3 7.5 12 3l9 4.5v9L12 21l-9-4.5zM3 7.5l9 4.5 9-4.5M12 12v9",
  tag: "M3 3h8l10 10-8 8L3 11zM7.5 7.5h.01",
  build: "M4 21V4h10v17M14 9h6v12M7 8h1.5M7 12h1.5M7 16h1.5M17 13h.5M17 17h.5",
  camera: "M4 8.5h3l1.7-2h6.6L17 8.5h3v10.5H4zM12 16.5a3 3 0 100-6 3 3 0 000 6z",
  dots: "M5 12h.01M12 12h.01M19 12h.01",
};

/** Offered in the category editor, in Reimburse's order. */
export const CATEGORY_ICONS = ["receipt", "plane", "train", "car", "fuel", "road", "parking", "bed", "meal", "box", "tag", "build", "camera", "dots"];

/** Defaults for the stock names; a category's own saved icon always wins. */
const BY_NAME: Record<string, string> = {
  Airfare: "plane", Train: "train", Lodging: "bed", Meals: "meal", "Ground transport": "car",
  Fuel: "fuel", Mileage: "road", Parking: "parking", Supplies: "box", Other: "dots",
};

export function categoryIconKey(c?: { icon?: string; name?: string } | null): string {
  return (c && ((c.icon && P[c.icon] ? c.icon : undefined) || BY_NAME[c.name ?? ""])) || "dots";
}

export function CategoryIcon({ name, size = 18 }: { name: string; size?: number }) {
  const d = P[name] ?? P.dots;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {/* One path per subpath, as Reimburse draws them. */}
      {d.split("M").filter(Boolean).map((seg, i) => <path key={i} d={"M" + seg} />)}
    </svg>
  );
}

/** The sidebar's Expenses entry. */
export function ExpensesIcon({ size = 18 }: { size?: number }) {
  return <CategoryIcon name="receipt" size={size} />;
}
